"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.mapPassengersToVehicles = mapPassengersToVehicles;
const legacy_enums_1 = require("@daytrip/legacy-enums");
const legacy_utils_1 = require("@daytrip/legacy-utils");
const utils_1 = require("@daytrip/utils");
function addRequiredCapacity(config) {
    return {
        ...config,
        requiredCapacity: config.adultsCount + config.childrenCount + config.additionalLuggagePairs,
    };
}
function mapPassengersToVehicles({ passengers, vehicles, }) {
    const additionalLuggage = (0, legacy_utils_1.getAdditionalLuggageCount)(passengers);
    const requiredSpace = passengers.length + additionalLuggage;
    const allVehiclesCapacity = vehicles.reduce((acc, v) => acc + legacy_enums_1.VehicleTypesCapacity[v], 0);
    const allChildren = passengers.filter((p) => p.type === legacy_enums_1.PassengerType.Child);
    const allAdults = passengers.filter((p) => p.type === legacy_enums_1.PassengerType.Adult);
    const lead = passengers.find((p) => p.type === legacy_enums_1.PassengerType.Lead);
    // sort vehicles by capacity, to put lead passenger and additional luggage to the bigger car
    // and to make config independent from the vehicles type order (so same result for Van+Sedan and Sedan+Van)
    const vehiclesData = vehicles
        .map((v, i) => ({ vehicle: v, originalIndex: i }))
        .sort((v1, v2) => legacy_enums_1.VehicleTypesCapacity[v2.vehicle] - legacy_enums_1.VehicleTypesCapacity[v1.vehicle]);
    if (!lead) {
        throw new Error("Lead passenger missing");
    }
    if (vehiclesData.length === 1) {
        const isOverloaded = requiredSpace > allVehiclesCapacity;
        return [
            addRequiredCapacity({
                vehicleType: vehiclesData[0].vehicle,
                adultsCount: allAdults.length + 1, // adults + lead
                childrenCount: allChildren.length,
                childSeats: allChildren.map((c) => c.childSeatType).filter((type) => (0, utils_1.isNotUndefinedOrNull)(type)),
                additionalLuggagePairs: additionalLuggage,
                isOverloaded,
                passengers: [lead, ...allAdults, ...allChildren],
            }),
        ];
    }
    const vehicleConfigs = vehiclesData.map((vehicleData, i) => {
        return {
            vehicleType: vehicleData.vehicle,
            adultsCount: allAdults.length > 0 || i === 0 ? 1 : 0,
            childrenCount: 0,
            childSeats: [],
            additionalLuggagePairs: 0,
            isOverloaded: false,
            passengers: i === 0 ? [lead] : allAdults.length > 0 ? [allAdults.shift()] : [],
        };
    });
    const toSplit = {
        additionalLuggagePairs: additionalLuggage,
        childrenCount: allChildren.length,
        adultsCount: allAdults.length,
    };
    // first put luggage to the first car with available space
    let vehicleIndex = 0;
    let skippedVehicles = 0;
    while (toSplit.additionalLuggagePairs > 0) {
        const currentLoad = vehicleConfigs[vehicleIndex].passengers.length + vehicleConfigs[vehicleIndex].additionalLuggagePairs;
        const currentVehicleCapacity = legacy_enums_1.VehicleTypesCapacity[vehicleConfigs[vehicleIndex].vehicleType];
        if (currentLoad >= currentVehicleCapacity) {
            vehicleIndex = vehicleIndex === vehicles.length - 1 ? 0 : vehicleIndex + 1;
            skippedVehicles += 1;
            if (skippedVehicles > vehicles.length) {
                break;
            }
            continue;
        }
        vehicleConfigs[vehicleIndex].additionalLuggagePairs += 1;
        toSplit.additionalLuggagePairs -= 1;
        skippedVehicles = 0;
        if (currentLoad + 1 >= currentVehicleCapacity) {
            vehicleIndex = vehicleIndex === vehicles.length - 1 ? 0 : vehicleIndex + 1;
        }
    }
    // spread all the children evenly across the vehicles with available space
    vehicleIndex = 0;
    skippedVehicles = 0;
    while (toSplit.childrenCount > 0) {
        const currentLoad = vehicleConfigs[vehicleIndex].passengers.length + vehicleConfigs[vehicleIndex].additionalLuggagePairs;
        const currentVehicleCapacity = legacy_enums_1.VehicleTypesCapacity[vehicleConfigs[vehicleIndex].vehicleType];
        if (currentLoad >= currentVehicleCapacity) {
            vehicleIndex = vehicleIndex === vehicles.length - 1 ? 0 : vehicleIndex + 1;
            skippedVehicles += 1;
            if (skippedVehicles > vehicles.length) {
                break;
            }
            continue;
        }
        vehicleConfigs[vehicleIndex].childrenCount += 1;
        toSplit.childrenCount -= 1;
        skippedVehicles = 0;
        const child = allChildren.shift();
        const seatType = child.childSeatType;
        if ((0, utils_1.isNotUndefinedOrNull)(seatType)) {
            vehicleConfigs[vehicleIndex].childSeats.push(seatType);
        }
        vehicleConfigs[vehicleIndex].passengers.push(child);
        vehicleIndex = vehicleIndex === vehicles.length - 1 ? 0 : vehicleIndex + 1;
    }
    // spread all the adults evenly across the vehicles with available space
    // keep vehicleIndex
    skippedVehicles = 0;
    while (toSplit.adultsCount > 0) {
        const currentLoad = vehicleConfigs[vehicleIndex].passengers.length + vehicleConfigs[vehicleIndex].additionalLuggagePairs;
        const currentVehicleCapacity = legacy_enums_1.VehicleTypesCapacity[vehicleConfigs[vehicleIndex].vehicleType];
        if (currentLoad >= currentVehicleCapacity) {
            vehicleIndex = vehicleIndex === vehicles.length - 1 ? 0 : vehicleIndex + 1;
            skippedVehicles += 1;
            if (skippedVehicles > vehicles.length) {
                break;
            }
            continue;
        }
        vehicleConfigs[vehicleIndex].adultsCount += 1;
        toSplit.adultsCount -= 1;
        skippedVehicles = 0;
        vehicleConfigs[vehicleIndex].passengers.push(allAdults.shift());
        vehicleIndex = vehicleIndex === vehicles.length - 1 ? 0 : vehicleIndex + 1;
    }
    // spread remaining items evenly across vehicles
    // at this point vehicles get overloaded
    vehicleIndex = 0;
    Object.entries(toSplit).forEach((entry) => {
        const key = entry[0];
        const count = entry[1];
        for (let i = 0; i < count; i++) {
            vehicleConfigs[vehicleIndex][key] += 1;
            vehicleConfigs[vehicleIndex].isOverloaded = true;
            toSplit[key] -= 1;
            if (key === "childrenCount") {
                const seatType = allChildren[toSplit[key]].childSeatType;
                if (seatType) {
                    vehicleConfigs[vehicleIndex].childSeats.push(seatType);
                }
                vehicleConfigs[vehicleIndex].passengers.push(allChildren.shift());
            }
            if (key === "adultsCount") {
                vehicleConfigs[vehicleIndex].passengers.push(allAdults.shift());
            }
            vehicleIndex = vehicleIndex === vehicles.length - 1 ? 0 : vehicleIndex + 1;
        }
    });
    // sort vehicles back to the original order
    const result = new Array(vehiclesData.length);
    vehiclesData.forEach((vehicleData, currentIndex) => {
        result[vehicleData.originalIndex] = vehicleConfigs[currentIndex];
    });
    return result.map(addRequiredCapacity);
}
//# sourceMappingURL=mapPassengersToVehicles.js.map